import { getUserProfile } from 'helpers/userProfile';
import { t } from 'i18next';
import {
  ReactElement, useEffect, useMemo, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { colors } from '~/styles/colors';
import { useFilterContext } from '../contexts/FilterContext';
import { ThermalComfortCountInfo, useThermalComfortDataContext } from '../contexts/ThermalComfortDataContext';
import { handleFilterStateRoute } from '../helpers/filters';
import { normalizeNumber } from '../helpers/numbers';
import { canViewThermalComfort } from '../helpers/thermalComfort';
import { BigNumberCard } from './components/Card';
import { GenericDescriptionTooltipContent } from './styles';

interface ThermalComfortProps {
  hasThermalComfort: boolean;
  loading: boolean;
}

export function ThermalComfort({ hasThermalComfort, loading }: Readonly<ThermalComfortProps>): ReactElement {
  const history = useHistory();
  const { data, loading: loadingThermalComfort } = useThermalComfortDataContext();
  const [thermalComfortInfo, setThermalComfortInfo] = useState<{ percentageComfortable: number, comfortableUnits: number } | undefined>();
  const { selectedOptions, client } = useFilterContext();
  const [profile] = useState(getUserProfile());

  const generalLoading = useMemo(
    () => loading || (loadingThermalComfort && hasThermalComfort && canViewThermalComfort(profile)),
    [hasThermalComfort, loading, loadingThermalComfort],
  );

  function parseThermalComfortInfo(data: ThermalComfortCountInfo | undefined) {
    if (data) {
      const comfortableUnits = data.comfortable;
      const total = data.comfortable + data.aboveLimit + data.belowLimit;

      if (Number(total === 0)) {
        setThermalComfortInfo({
          percentageComfortable: 0,
          comfortableUnits: 0,
        });
        return;
      }

      const percentageComfortable = Math.floor(comfortableUnits * 100 / total);

      setThermalComfortInfo({
        percentageComfortable,
        comfortableUnits,
      });
    }
  }

  function indicatorColor(): string {
    if (!thermalComfortInfo) return colors.icon['disabled-300'];

    if (thermalComfortInfo.percentageComfortable >= 85) return colors.state.success.base;
    if (thermalComfortInfo.percentageComfortable >= 45) return colors.state.warning.base;
    return colors.state.danger.base;
  }

  function onClickDetails(): void {
    history.push('/analise/ambientes',
      handleFilterStateRoute(client, selectedOptions, profile));
  }

  useEffect(() => {
    if (canViewThermalComfort(profile)) {
      parseThermalComfortInfo(data);
    }
  }, [data]);

  return (
    <BigNumberCard
      id="thermal-comfort"
      icon="Thermometer2Icon"
      number={`${normalizeNumber(thermalComfortInfo?.percentageComfortable)}%`}
      description={{
        text: t('confortoTermicoMedido'),
        tooltip: <ThermalComfortTooltip />,
      }}
      subDescription={{
        number: normalizeNumber(thermalComfortInfo?.comfortableUnits),
        text: t('unidadesDentroDaFaixaIdealMedida'),
      }}
      loading={generalLoading}
      disabledTooltip={false}
      indicatorColor={indicatorColor()}
      needUpgrade={hasThermalComfort ? undefined : 'thermalComfort'}
      onClickDetails={onClickDetails}
      dataTestId="big-number-card-thermal-comfort"
      comingSoon={!canViewThermalComfort(profile)}
      beta
    />
  );
}

function ThermalComfortTooltip() {
  return (
    <GenericDescriptionTooltipContent style={{ height: 80, width: 240 }}>
      <span>{t('numeroIndicaPercentualUnidades')}</span>
    </GenericDescriptionTooltipContent>
  );
}
